/* global Promise */

require('es6-promise').polyfill();
require('isomorphic-fetch');

import queryString from 'query-string';
import environmentNames from '../environmentNames';
import { reportError } from './ErrorReporting'

export default {
    API_ROOT: '/ICSXapi/content/',

    responseCategories: {
        OK: 'OK',
        NOT_LOGGED_IN: 'NOT_LOGGED_IN',
        ENDPOINT_UNAVAILABLE: 'ENDPOINT_UNAVAILABLE',
        SERVER_ERROR: 'SERVER_ERROR',
        RESPONSE_PARSING_ERROR: 'RESPONSE_PARSING_ERROR',
        FORBIDDEN: 'FORBIDDEN',
        OFFLINE: 'OFFLINE',
        OTHER: 'OTHER'

    },

    WORKFLOW_CALL_STATUS: {
        OK: 'OK',
        IN_PROGRESS: 'IN_PROGRESS',
        WAITING_FOR_RETRY: 'WAITING_FOR_RETRY',
        NOT_STARTED: 'NOT_STARTED'
    },

    getWindowLocation() {
        return window.location ? window.location : undefined;
    },

    /**
     * Prepends the https://www.mydomain.com part of the url to the path passed in.
     *
     * @param {string} path Must start with a /!
     * @returns {string}
     */
    prependDomain: function prependDomain(path) {
        const location = this.getWindowLocation();
        const { hostname, protocol, port } = location;
        let methodAndHost = `${ protocol }//${ hostname }`;

        if (port !== 80 && port !== 443) {
            methodAndHost += `:${port}`;
        }

        return `${ methodAndHost }${ path }`;
    },

    /**
     * Tries to execute a request.
     * Resolves with the parsed JSON body or rejects with normalized errors.
     * State is updated to reflect current network, server, login status when relevant.
     *
     * @param {Request} request
     * @return {Promise}
     */
    executeRequest(request) {
        return fetch(request)
            .then((response) => this.handleApiResponse(response))
            .catch(ex => {
                const isOfflineError = this.isOfflineError(ex);

                if (isOfflineError) {
                    this.updateOnlineStatusIfNeeded(false);
                    throw {errorType: this.responseCategories.OFFLINE, response: undefined, error: ex};
                }

                reportError(ex);

                throw ex;
            });
    },
    
    /**
     * Returns a Promise which resolves with the parsed JSON data or rejects with an object on this form:
     * {
     *      errorType,  // String
     *      error,      // Original Error, if any, else undefined
     *      response    // Original Response object
     * }
     * @param response
     * @returns {Promise}
     */
    handleApiResponse(response) {
        const loggedIn = this.isLoggedIn(response);
        const online = this.isOnline(response);

        this.updateLoggedInStatusIfNeeded(loggedIn);
        this.updateOnlineStatusIfNeeded(online);

        return new Promise((resolve, reject) => {
            let errorType,
                error;

            if (!online) {
                errorType = this.responseCategories.ENDPOINT_UNAVAILABLE;
            } else if (!loggedIn) {
                errorType = this.responseCategories.NOT_LOGGED_IN;
            } else if (response.status >= 500) {
                errorType = this.responseCategories.SERVER_ERROR;
            } else if (response.status >= 400) {
                errorType = this.responseCategories.ENDPOINT_UNAVAILABLE;
            } else if (response.status !== 200) {
                errorType = this.responseCategories.OTHER;
            }
            if (!errorType) {
                try {
                    response.json()
                        .then(parsedData => {
                            resolve(parsedData);
                        })
                        .catch((ex) => {
                            //reportError(ex);
                            reject({errorType: this.responseCategories.RESPONSE_PARSING_ERROR, response, error: ex});
                        });
                } catch (ex) {
                    error = ex;
                    errorType = this.responseCategories.RESPONSE_PARSING_ERROR;
                }
            }

            if (errorType) {
                reject({errorType, error, response});
            }
        });
    },

    /**
     * Retries a request a given number of times, at the specified interval.
     *
     * @param {Function} requestFunction Any function that returns a promise.
     * @param retriesToDo
     * @param msToWait
     * @returns {Promise}
     */
    retryRequest (requestFunction, retriesToDo = 3, msToWait = this.getDefaultInterval()) {
        let retriesDone = 0;
        let intervalHandle;

        return new Promise((resolve, reject) => {
            intervalHandle = setInterval(() => {
                retriesDone++;

                requestFunction()
                    .then((res) => {
                        clearInterval(intervalHandle);
                        resolve(res);
                    })
                    .catch((ex) => {
                        // Ignore errors until we are at the final iteration.
                        if (retriesDone === retriesToDo) {
                            clearInterval(intervalHandle);
                            reject(ex);
                        }
                    });
            }, msToWait);

        });
    },

    getDefaultInterval () {
        return process.env.NODE_ENV === environmentNames.UNIT_TESTING ? 50 : 5000;
    },

    /**
     * Checks if the response indicates that the user is not logged in.
     *
     * @param res
     * @returns {boolean}
     */
    isLoggedIn(res) {
        return res.status !== 401;
    },

    /**
     * TODO: Add actual checking. Just have to figure out the best way...
     * @param res
     * @returns {boolean}
     */
    isOnline(res) {
        return res === res;
    },

    isOfflineError(error) {
        if (error.message === 'Failed to fetch') {
            return true;
        }

        return false;
    },

    updateOnlineStatusIfNeeded () {

    },

    updateLoggedInStatusIfNeeded () {

    },

    /**
     * If the error is on the format returned by ApiService.executeRequest
     * @param error
     * @returns {boolean}
     */
    isResponseError(error) {
        // All errors that have come from the handleApiResponse have a response property defined
        // network failures are plain Error objects
        return Object.prototype.hasOwnProperty.call(error, 'errorType');
    },

    /**
     ********************************************************************************************
     */
    makeJsonPostRequest(url, data) {
        const request = new Request(
            url,
            {
                credentials: 'same-origin',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }
        );

        return request;
    },
    makeFormDataPostRequest(url, data = {}) {
        const formData = queryString.stringify(data);

        const headers = new Headers();
        headers.append('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');

        const request = new Request(
            url,
            {
                body: formData,
                credentials: 'same-origin',
                headers,
                method: 'POST'
            }
        );

        return request;
    },
    storeBigDataCartoonist(s3Key, title, contentItemId, submitId, exerciseId, clipartReference, thumbnailS3Key, autosave, usedContentIds) {
        const url = `${this.API_ROOT}StoreCartoonistURL`;
        const data = {
            s3Key,
            title,
            contentItemId,
            submitId,
            exerciseId,
            clipart_reference: clipartReference,
            thumbnailS3Key,
            autosave,
            usedContentIds
        };

        const request = this.makeFormDataPostRequest(url, data);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(this.makeFormDataPostRequest(url, data))
            ));
    },
    storeCartoonist(title, contentItemId, submitId, exerciseId, jsonData, clipartReference, thumbnailS3Key, autosave = false, isAudio = false) {
        const url = `${this.API_ROOT}StoreCartoonist`;
        const data = {
            title,
            contentItemId,
            submitId,
            exerciseId,
            textcontent: jsonData,
            clipart_reference: clipartReference,
            thumbnailS3Key,
            autosave,
            isAudio
        };

        const request = this.makeFormDataPostRequest(url, data);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(this.makeFormDataPostRequest(url, data))
            ));
    },
    publish(submitId, isAudioJob) {
        const url = `/ICSXapi/work/Publish`;
        const data = {
            setPublic: true,
            exportDestination: 'homepage',
            clipartId: 123456,
            submitId,
            isAudioJob
        };

        const request = this.makeFormDataPostRequest(url, data);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(this.makeFormDataPostRequest(url, data))
            ));
    },
    exportCartoonist(submitId, textContent, duration) {
        let url = `/ICSXapi/work/Export`;
        const data = {
            submitId,
            textContent,
            duration
        };

        const request = this.makeFormDataPostRequest(url, data);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(this.makeFormDataPostRequest(url, data))
            ));
    },
    unlockProduction(submitId) {
        let url = `/ICSXapi/work/UnLock`;
        const data = {
            submitId
        };

        const request = this.makeFormDataPostRequest(url, data);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(this.makeFormDataPostRequest(url, data))
            ));
    },
    storeBytes(title, bytes) {
        let url = `${this.API_ROOT}StoreRecording`;

        const data = {
            title,
            data:bytes
        };

        const request = this.makeFormDataPostRequest(url, data);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(this.makeFormDataPostRequest(url, data))
            ));
    },
    loadCartoonistContent(contentRevisionId, contentItemId, submitId) {
        let path = `${this.API_ROOT}GetTextContent?contentRevisionId=${contentRevisionId}&contentItemId=${contentItemId}&submitId=${submitId}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    loadMediaFiles(contentType, itemsPerPage) {
        let path = `${this.API_ROOT}GetMediaFiles?type=${contentType}&hpp=${itemsPerPage}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    searchContent(text, lng, list, themeElementType) {
        let url = `${this.API_ROOT}SearchClipartContent`;
        var data = {
            s:text,
            lng,
            clipartList: list,
            hpp:1000
        };

        if (themeElementType)
            data.themeElementType = themeElementType;

        const request = this.makeFormDataPostRequest(url, data);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(this.makeFormDataPostRequest(url, data))
            ));
    },
    loadAssignmentFolder(id) {
        let path = `/ICSXapi/work/GetAssignmentMediaFiles?id=${id}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getBlobFromBase64Url(url) {
        const request = new Request(url, { credentials: 'same-origin' });
        return fetch(request)
            .then((response) => {
                return response.blob()
                    .then(blob => {
                        return blob;
                    });
            });
    },
    loadClipartJSON(contentItemId) {
        let path = `${this.API_ROOT}GetClipart?contentItemId=${contentItemId}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getExercisesTitle (exerciseId) {
        let path = `/ICSXapi/work/Describe?id=${exerciseId}&type=exercise`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getSubmitTitle (submitId) {
        let path = `/ICSXapi/work/Get?id=${submitId}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getAvailableCliparts () {
        let path = `/ICSXapi/content/GetAvailableCliparts`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getBasicSessionProperties () {
        let path = `/ICSXapi/users/GetBasicSessionInfo`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getSessionProperties () {
        let path = `/ICSXapi/users/GetSessionInfo`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getSessionInfo () {
        let path = `/ICSXapi/users/HasActiveSession`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getLastUploadedMediaFileId () {
        let path = `/ICSXapi/users/GetLastUploadedFile`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getUploadingMediaFileStatus (contentItemId) {
        let path = `/ICSXapi/file/CheckStatus?contentItemId=${contentItemId}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    hasAccess (contentItemId, contentRevisionId) {
        let path = `/ICSXapi/content/HasAccess?contentItemId=${contentItemId}&contentRevisionId=${contentRevisionId}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getExportStatus (jobId) {
        let path = `/ICSXapi/work/GetExportStatus?jobId=${jobId}&cacheBreaker=${Math.random()}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getSessionToken () {
        let path = `/ICSXapi/exercise/RequestTokenCartoonist`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    doLoginWithToken (token, id) {
        let path = `/ICSXapi/users/DoCartoonistAppLogin?token=${token}&id=${id}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    setItemProperty (itemProperty, userId, value) {
        let path = `/ICSXapi/metadata/SetItemValue?itemProperty=${itemProperty}&objectId=${userId}&value=${encodeURIComponent(JSON.stringify(value))}&flushCache=true`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getVideoOriginals (ids) {
        var idsList = '';
        var i;

        for (i=0; i<ids.length; i++) {
            if (i==0)
                idsList = idsList + 'ids=' + ids[i];
            else
                idsList = idsList + '&ids=' + ids[i];
        }

        let path = `/ICSXapi/content/GetVideoOriginals?${idsList}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    getSignedContent (ids) {
        var idsList = '';
        var i;

        for (i=0; i<ids.length; i++) {
            if (i==0)
                idsList = idsList + 'ids=' + ids[i];
            else
                idsList = idsList + '&ids=' + ids[i];
        }

        let path = `/ICSXapi/content/GetSignedContentURLs?${idsList}`;
        const request = new Request(path, { credentials: 'same-origin' });

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },

    /*******************  New universes+themes functionality *************************/
    loadUniverses() {
        let path = `https://api.creaza.com/content/list/universe`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    loadThemes() {
        let path = `https://api.creaza.com/content/list/theme`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    loadThemesFromUniverse(universe) {
        let path = `https://api.creaza.com/content/list/theme/${universe}`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    loadCurrentTheme(theme, locale) {
        let path = (locale && locale.length > 0) ? `https://api.creaza.com/content/${theme}/${locale}` : `https://api.creaza.com/content/${theme}`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    searchNewContent(language, category, keyword, pageNumber, pageSize) {
        let path = `https://api.creaza.com/content/search/${language}/${category}/${keyword}/${pageNumber}/${pageSize}`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    loadMoods() {
        let path = `https://api.creaza.com/content/translations/moods`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    loadThemeCategories() {
        let path = `https://api.creaza.com/content/translations/categories`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    loadCartoonistCaptions() {
        let path = `https://api.creaza.com/content/translations/editor`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    listVideoThemes() {
        let path = `https://api.creaza.com/content/list/video-theme`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    loadVideoTheme(theme) {
        let path = `https://api.creaza.com/content/video/${theme}`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    },
    searchVideo(language, keyword, pageNumber, pageSize) {
        let path = `https://api.creaza.com/content/search/video/${language}/${keyword}/${pageNumber}/${pageSize}`;
        const request = new Request(path);

        return this.executeRequest(request)
            .catch(() => this.retryRequest(
                () => this.executeRequest(request)
            ));
    }
};



/** WEBPACK FOOTER **
 ** ./src/services/ApiService.js
 **/